/* eslint-disable prettier/prettier */
/* eslint-disable @typescript-eslint/prefer-for-of */
/** @jsxImportSource @emotion/react */
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCartShipGeoCode,
  updateShippingAddress,
  updateBillingAddress,
  setCartShippingAddress,
  setCartBillingAddress,
} from '../../../autoshipState/autoshipSlice';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import t from '@rcm/i18n-client';
import {
  selectCartGeoCode,
  selectCityDropdown,
  selectCartShippingAddressLine2,
  selectShippingAddressLine2,
  selectCartShippingName,
  selectCartPhone,
} from 'cart-ui-react/src/autoshipState/selectors';
import getAppState from 'app-state';

type Address = {
  addressLine1: string;
  addressLine2?: string;
  name?: string;
  state: string;
  country: string;
  postalCode: string;
  geoCode?: string;
  isCartV2?: boolean;
  phone?: string;
};

const VerifyAddressModal = ({
  openModal,
  setOpenModal,
  suggestedFedexAddress,
  setSuggestedFedexAddress,
  shippingAddress = null,
  billingAddress = null,
  isCartV2 = false,
}) => {
  const dispatch = useDispatch();
  const [address, setAddress] = useState<Address | null>(null);
  const modalAddressLine2 = useSelector(isCartV2 ? selectCartShippingAddressLine2 : selectShippingAddressLine2);
  const nameLine = useSelector(selectCartShippingName);
  const cityDropdown = useSelector(selectCityDropdown);
  const phone = useSelector(selectCartPhone);
  const isUS = getAppState('site.countryIso3') === 'USA';
  const cartGeoCode = useSelector(selectCartGeoCode);
  useEffect(() => {
    setAddress(shippingAddress ?? billingAddress);

    if (!isUS) {
        setSuggestedFedexAddress({});
        setOpenModal(false);
    }
  }, []);

  const keepAddress = () => {
    setSuggestedFedexAddress({});
    setOpenModal(false);
  };

  // the data we get back from Fedex is not standardized, in some countries the address
  // lines are juxtaposed.  This was based on what was being done in shipping-verifications.js
  // but this accounts for more edge cases and is for Address line 1.
  const addressLine1 = Array.isArray(suggestedFedexAddress?.StreetLines)
    ? suggestedFedexAddress?.StreetLines[0] === address?.addressLine2
      ? suggestedFedexAddress?.StreetLines[1]
      : suggestedFedexAddress?.StreetLines[0]
    : suggestedFedexAddress?.StreetLines;

  // use address line 2 entered, if fedex return nothing
  const addressLine2 = Array.isArray(suggestedFedexAddress?.StreetLines)
    ? suggestedFedexAddress.StreetLines[1] === undefined
      ? modalAddressLine2
      : suggestedFedexAddress.StreetLines[1]
    : address?.addressLine2;

  const setGeoCode = (event) => {
    dispatch(setCartShipGeoCode(event.target.value));
  }

  const useSuggestedAddress = () => {
    const tempAddressData: any = isCartV2
      ? {
          nameLine: nameLine ?? address?.name,
          phone: phone ?? address?.phone,
          addressLine1: addressLine1,
          addressLine2: addressLine2,
          city: suggestedFedexAddress.City,
          state: suggestedFedexAddress.StateOrProvinceCode,
          postalCode: suggestedFedexAddress.PostalCode,
          country: address?.country,
          geoCode: address?.geoCode ?? '',
        }
      : {
          name: address?.name,
          addressLine1: addressLine1,
          addressLine2: addressLine2,
          city: suggestedFedexAddress.City,
          state: suggestedFedexAddress.StateOrProvinceCode,
          postalCode: suggestedFedexAddress.PostalCode,
          country: address?.country,
          geoCode: address?.geoCode ?? '',
        };

    dispatch(
      isCartV2
        ? shippingAddress
          ? setCartShippingAddress(tempAddressData)
          : setCartBillingAddress(tempAddressData)
        : shippingAddress
        ? updateShippingAddress(tempAddressData)
        : updateBillingAddress(tempAddressData)
    );
    setSuggestedFedexAddress({});

    setOpenModal(false);
  };

  return (
    <Dialog open={openModal && suggestedFedexAddress.StateOrProvinceCode} onClose={keepAddress}>
      <DialogTitle>
        <Typography sx={{ fontSize: '2.125rem' }}>{t('Suggested Address:')}</Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            fontWeight: 400,
            color: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '1.5rem',
          }}
        >
          <Typography sx={{ fontSize: '1.5rem' }}>{addressLine1}</Typography>
          <Typography sx={{ fontSize: '1.5rem' }}>{addressLine2}</Typography>
          <Typography sx={{ fontSize: '1.5rem' }}>
            {suggestedFedexAddress?.City}
            {`${address?.state?.length > 0 ? ', ' + suggestedFedexAddress.StateOrProvinceCode : ''}`}
          </Typography>
          <Typography sx={{ fontSize: '1.5rem' }}>{suggestedFedexAddress?.PostalCode}</Typography>
          {
            isUS ? 
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {t('Tax Area:')}
              <select style={{ marginTop: '4px' }} value={cartGeoCode} onChange={setGeoCode}>
                {
                  cityDropdown.map((i) => <option value={i[2]}>{i[1]}</option>)
                }
              </select>
            </div>
            : null
          }
        </Box>
      </DialogContent>
      <DialogActions>
        <Button id="keep-original-address-mocha" variant="outlined" onClick={keepAddress} sx={{ fontSize: '14px' }}>
          {t('Keep Original Address')}
        </Button>
        <Button variant="contained" onClick={useSuggestedAddress} sx={{ fontSize: '14px' }}>
          {t('Use Suggested Address')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default VerifyAddressModal;
